import {
  MenuGroupProps as ChakraMenuGroupProps,
  chakra,
  forwardRef,
  useMenuStyles,
} from '@chakra-ui/react';
import classnames from 'classnames';
import React from 'react';

import { OmitChakraProps } from '../../utils/typeUtils';

export interface MenuGroupProps
  extends OmitChakraProps<ChakraMenuGroupProps, 'title'> {
  /**
   * A label describing the menu group. This is pretty much the equivalent of
   * MenuGroup.title, but we found it to be too limiting for Orbit's use cases.
   * This way we can prevent the use of title to avoid rendering the original.
   */
  label?: React.ReactNode;
  /**
   * A short description below the title that gives further context.
   */
  labelDescription?: React.ReactNode;
}

export const MenuGroup = forwardRef<MenuGroupProps, 'div'>((props, ref) => {
  const { children, label, labelDescription, className, ...rest } = props;

  const styles = useMenuStyles();

  return (
    <chakra.div
      ref={ref}
      // eslint-disable-next-line tailwindcss/no-custom-classname
      className={classnames('orbit-menu__group', className)}
      role="group"
      {...rest}
      __css={styles.group}
    >
      {label && (
        <chakra.div
          __css={styles.labelContainer}
          // eslint-disable-next-line tailwindcss/no-custom-classname
          className="orbit-menu__group_header"
        >
          <chakra.p data-testid="option-group-label" __css={styles.label}>
            {label}
          </chakra.p>
          {labelDescription && (
            <chakra.p
              data-testid="option-group-label-description"
              __css={styles.labelDescription}
            >
              {labelDescription}
            </chakra.p>
          )}
        </chakra.div>
      )}
      {children}
    </chakra.div>
  );
});

MenuGroup.displayName = 'MenuGroup';
